import { FusionQueryProvider } from '@devxdiscover/devhub-common';
import { createEnvHash } from '../util';

/**
 * The entity types that are tracked for last update
 */
export enum LastUpdateEntityType {
  applications = 'applications',
  components = 'components',
  apis = 'apis',
  gitHubUsers = 'gitHubUsers',
  gitHubTeams = 'gitHubTeams',
  gitHubRepositories = 'gitHubRepositories',
  gitHubOrganizations = 'gitHubOrganizations',
  devHubTemplates = 'devHubTemplates',
}

/**
 * A service to manage the import state of the Fusion catalog
 */
export class FusionCatalogImportService {
  private readonly queryProvider: FusionQueryProvider;

  constructor(queryProvider: FusionQueryProvider) {
    this.queryProvider = queryProvider;
  }

  /**
   * Gets the provider name
   * @private
   */
  private getProviderName(): string {
    if (process.env.ENV_TYPE) {
      return process.env.ENV_TYPE;
    }
    return `local-${createEnvHash()}`;
  }

  /**
   * Retrieves the last updated date for the given entity type
   * @param entityType The entity type
   */
  public async getLastUpdatedDate(
    entityType: LastUpdateEntityType,
  ): Promise<Date | null> {
    const query = `
      query ${entityType}LastImportState($provider: String!) {
        devhub {
          catalog {
            import(provider: $provider) {
              lastImportState {
                entity:${entityType}
              }
            }
          }
        }
      }
    `;
    const response = await this.queryProvider.query<{
      devhub: {
        catalog: { import: { lastImportState: { entity: Date | null } } };
      };
    }>(query, {
      provider: this.getProviderName(),
    });
    return response.data.devhub.catalog.import.lastImportState.entity;
  }

  /**
   * Retrieves the last updated date for the given entity type
   * @param entityType The entity type
   * @param updateDate The date to set the last updated date to
   */
  public async setLastUpdatedDate(
    entityType: LastUpdateEntityType,
    updateDate: Date | null,
  ): Promise<boolean> {
    const query = `
      mutation ${entityType}UpdateLastImportState( $input: DevHubUpdateLastImportInput!, $provider: String!) {
        devhub {
          catalog {
            updateLastImport(input: $input, provider: $provider) {
              message
              success
            }
          }
        }
      }
    `;
    const response = await this.queryProvider.mutate<{
      devhub: {
        catalog: { updateLastImport: { message: string; success: boolean } };
      };
    }>(query, {
      input: {
        [entityType]: updateDate === null ? null : updateDate.toISOString(),
      },
      provider: this.getProviderName(),
    });
    return response.data?.devhub.catalog.updateLastImport.success ?? false;
  }
}
